import { Component, OnInit, DestroyRef, inject, ChangeDetectorRef, ViewChild, AfterViewInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Endpoint } from '../../../common/models/endpoint-group.model';
import { StrapiService } from '../../../common/services/strapi.service';
import { SearchService } from '../../../common/services/search.service';
import { filter, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SiteService } from '../../../common/services/site.service';
import { AuthService } from '../../../common/services/auth.service';
import { Subject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap';
import { AppSettings } from '../../../common/constants/appSettings.constants';
import { Category } from '../../../common/models/category.model';
import { CategoryWithGroups } from '../../../common/models/category-group.model';
import { environment } from '../../../../environments/environment';
import { APIDomain, ContentType } from '../../../common/models/site.model';
import { NavItemsComponent } from '../nav-items/nav-items.component';
import { DocsNavItem } from '../../../common/models/nav.model';
import { APIDomainVersion } from '../../../common/models/site.model';

@Component({
    selector: 'app-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['./side-nav.component.scss', '../common/side-nav-common.component.scss'],
    standalone: false
})
export class SideNavComponent implements OnInit, AfterViewInit {
    @ViewChild('sideNavClientLibraryAccordion', { static: false }) sideNavClientLibraryAccordion: NgbAccordionDirective;
    @ViewChild('navItemsComponent', { static: false }) navItemsComponent: NavItemsComponent;

    private destroyRef = inject(DestroyRef);
    searchInputChanged$: Subject<string> = new Subject<string>();
    settings = AppSettings;
    searchInput: string = '';
    navVisible = true;
    loading = false;
    selectedDomainVersion: APIDomainVersion = this.settings.general.defaultDomainVersion;
    coreVersions: string[] = [];
    allyVersions: string[] = [];
    selectedVersion: string = '';
    loggedIn = false;
    librariesByType: { [type: string]: any[] } = {};
    displayRecipes: boolean = environment.displayRecipes;

    // API Navigation
    navItems: Endpoint[] = [];
    groupedNavItems: any[] = [];

    // Docs Navigation
    docsNavItems: any[] = [];

    contentType: ContentType = 'api';
    public activeEndpoint: string | null = null;
    groupSeenKeys = new Set<string>();
    currentTheme: string = '';
    hoveredLib: number | null = null;
    renderedKeys: Set<string> = new Set();

    navForm = new FormGroup({
        selectedDomain: new FormControl('core')
    });

    // Track active nav items (endpoints or docs pages)
    activeItem: string = '';
    activeSlug: string = '';
    activeGroup: string = '';
    activeCategoryId: string = '';

    constructor(
        private strapiService: StrapiService,
        private siteService: SiteService,
        private searchService: SearchService,
        private authService: AuthService,
        private router: Router,
        private cd: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.siteService.activeItem$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((item: string) => {
            if (item) {
                this.activeItem = item;
            }
        });

        this.siteService.activeGroup$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((groupCode: string) => {
            if (groupCode) {
                this.activeGroup = groupCode;
                this.cd.detectChanges();
            }
        });
        
        this.siteService.currentDomainVersion$.pipe(
            distinctUntilChanged(),
            switchMap((domainVersion: APIDomainVersion) => {
                this.selectedDomainVersion = domainVersion;

                // Sync form controls when version changes externally (e.g. from search filters)
                const [domain, version] = domainVersion.split('_') as [APIDomain, string];
                if (this.navForm.get('selectedDomain')?.value !== domain) {
                    this.navForm.get('selectedDomain')?.setValue(domain, { emitEvent: false });
                }
                this.selectedVersion = version;

                this.navItems = [];
                this.groupedNavItems = [];
                return this.strapiService.getSideNavByDomainVersion(domainVersion);
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(data => {
            if (data.length > 0) {
                for (const endpoint of data) {
                    if (!endpoint.group) {
                        console.error('Missing group property in endpoint:', endpoint);
                    }
                    this.navItems.push({
                        label: endpoint.label,
                        uri: endpoint.uri,
                        slug: endpoint.slug,
                        version: endpoint.version,
                        domain: endpoint.domain,
                        method: endpoint.method,
                        group: endpoint.group,
                        groupCode: endpoint.groupCode,
                        apiSubdomain: endpoint.apiSubdomain,
                        category: endpoint.category,
                    });
                }

                this.groupedNavItems = this.groupAndSortNavItems(this.navItems);
                this.siteService.changeNav(this.groupedNavItems);

                if (this.activeEndpoint) {
                    const result = this.findEndpointHierarchyBySlug(this.groupedNavItems, this.activeEndpoint);
                    if (result) {
                        this.activeSlug = result.endpoint.slug;
                        this.activeGroup = result.group.groupCode;
                        this.activeCategoryId = result.category.categoryId;
                    }
                }
            }
        });

        this.siteService.currentNavDocs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((docs: any[]) => {
            this.docsNavItems = docs;
            // console.log('Docs nav items loaded:', this.docsNavItems);
        });

        this.siteService.contentType$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((type: ContentType) => {
            this.contentType = type;
            this.cd.detectChanges();
        });

        this.searchInputChanged$
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((input: string) => {
                this.searchService.setSearchQuery(input);
            });

        this.searchService.searchQuery$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(term => {
            this.searchInput = term;
        });

        this.siteService.navOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(state => {
            this.navVisible = state;
        });

        this.siteService.closeNavGroupsEvent.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.closeNavGroups();
        });

        this.siteService.theme$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((theme: string) => {
                    this.currentTheme = theme;
                    return this.strapiService.getAllCodelibraries();
                })
            )
            .subscribe(data => {
                this.librariesByType = data?.reduce((grouped: any, lib: any) => {
                    const type = lib.Type || '';
                    grouped[type] = grouped[type] || [];
                    grouped[type].push(lib);
                    return grouped;
                }, {});
            });

        this.authService.loggedIn$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(loggedIn => {
            this.loggedIn = loggedIn;
        });

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                if (window.innerWidth <= 768) {
                    this.siteService.closeSidebar();
                }
            });

        // Subscribe to available versions from siteService (populated once via cached HTTP call)
        this.siteService.coreVersions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(versions => {
            this.coreVersions = versions;
        });

        this.siteService.allyVersions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(versions => {
            this.allyVersions = versions;
        });

        // When selected domain changes, restore the last-used version for that domain
        this.navForm.get('selectedDomain')!.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((domain: string | null) => {
                const resolvedDomain = (domain ?? 'core') as APIDomain;
                this.siteService.changeDomain(resolvedDomain);
            });

        // Handle slug on first load and on every route change
        this.extractSlugFromUrl(this.router.url);

        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const url = event.urlAfterRedirects || event.url;
                this.extractSlugFromUrl(url);
            });
    }

    ngAfterViewInit(): void {
        const allDebugKeys = this.groupedNavItems
            .flatMap(cat => cat.groups)
            .flatMap(group => group.endpoints)
            .map(e => e.debugKey);

        const duplicates = allDebugKeys.filter((k, i, arr) => arr.indexOf(k) !== i);

        if (duplicates.length > 0) {
            console.warn('Duplicates found in rendered data:', duplicates);
        }
    }

    /**
     * Check if we have navigation data to display based on content type
     */
    hasNavData(): boolean {
        if (this.contentType === 'api') {
            return this.groupedNavItems && this.groupedNavItems.length > 0;
        } else if (this.contentType === 'docs') {
            return this.docsNavItems && this.docsNavItems.length > 0;
        }
        return false;
    }

    /**
     * Extract and set active navigation state from URL
     */
    private extractSlugFromUrl(url: string): void {
        // Check for API endpoint route (/e/slug)
        const apiMatch = url.match(/\/e\/([^\/?#]+)/);
        if (apiMatch && apiMatch[1]) {
            this.activeEndpoint = apiMatch[1];
            const result = this.findEndpointHierarchyBySlug(this.groupedNavItems, this.activeEndpoint);
            if (result) {
                this.activeItem = result.endpoint.slug;
                this.activeGroup = result.group.groupCode;
                this.activeCategoryId = result.category.categoryId;
            }
            return;
        }
    }

    /**
     * Find doc item and its parent category by slug
     */
    private findDocHierarchyBySlug(
        docs: DocsNavItem[],
        slug: string
    ): { categoryId: number; doc: DocsNavItem | { slug: string; title: string } } | null {
        for (const item of docs) {
            // Check if it's a standalone doc
            if (item.type === 'doc' && item.slug === slug) {
                return { categoryId: item.id, doc: item };
            }
            
            // Check children for categories
            if (item.type === 'category' && item.children) {
                const child = item.children.find(c => c.slug === slug);
                if (child) {
                    return { categoryId: item.id, doc: child };
                }
            }
        }
        return null;
    }

    private findEndpointHierarchyBySlug(
        grouped: CategoryWithGroups[],
        slug: string
    ): {
        endpoint: Endpoint & { debugKey: string },
        group: { group: string, groupCode: string },
        category: Category
    } | null {
        for (const category of grouped) {
            for (const group of category.groups) {
                const found = group.endpoints.find(ep => ep.slug === slug);
                if (found) {
                    return {
                        endpoint: found,
                        group: { group: group.group, groupCode: group.groupCode },
                        category: {
                            categoryId: category.categoryId,
                            categoryName: category.categoryName,
                            categoryPriority: category.categoryPriority
                        }
                    };
                }
            }
        }
        return null;
    }

    getThemeIcons(lib: any): { Default: any; Hover: any } {
        return {
            Default: lib.IconDefault,
            Hover: this.currentTheme === 'light-mode' ? lib.IconLight : lib.IconDark
        };
    }

    toggleSidebar(): void {
        this.siteService.toggleSidebar();
    }

    closeSidebar(): void {
        this.siteService.closeSidebar();
    }

    submitSearchInput(): void {
        this.searchInputChanged$.next(this.searchInput);
        this.closeSidebar();
    }

    doLogout(): void {
        this.authService.logout();
    }

    onGroupExpand(): void {
        this.groupSeenKeys.clear();
        this.renderedKeys.clear();
    }

    groupAndSortNavItems(endpoints: Endpoint[]): CategoryWithGroups[] {
        const categoryMap: Record<string, Category & {
            groups: Record<string, {
                group: string;
                groupCode: string;
                endpoints: (Endpoint & { debugKey: string })[];
            }>;
        }> = {};

        const seenKeys = new Set<string>();
        let index = 0;

        for (const endpoint of endpoints) {
            const hasCategory = endpoint.category && Object.keys(endpoint.category).length > 0;
            const categoryId = hasCategory ? endpoint.category.id : 'general';
            const categoryName = hasCategory ? endpoint.category.Name : 'General';
            const categoryPriority = hasCategory ? endpoint.category.Priority : 0;

            const categoryKey = `${categoryName}-${categoryId}`;
            const groupKey = `${endpoint.group}-${endpoint.groupCode}`;
            const debugKey = String(index++);

            if (seenKeys.has(debugKey)) {
                console.warn('Duplicate debugKey detected:', debugKey, endpoint);
            } else {
                seenKeys.add(debugKey);
            }

            if (!categoryMap[categoryKey]) {
                categoryMap[categoryKey] = {
                    categoryId,
                    categoryName,
                    categoryPriority,
                    groups: {},
                };
            }

            if (!categoryMap[categoryKey].groups[groupKey]) {
                categoryMap[categoryKey].groups[groupKey] = {
                    group: endpoint.group,
                    groupCode: endpoint.groupCode,
                    endpoints: [],
                };
            }

            categoryMap[categoryKey].groups[groupKey].endpoints.push({
                ...endpoint,
                debugKey,
            });
        }

        return Object.values(categoryMap)
            .map((category) => ({
                categoryId: category.categoryId,
                categoryName: category.categoryName,
                categoryPriority: category.categoryPriority,
                groups: Object.values(category.groups).map((group) => ({
                    group: group.group,
                    groupCode: group.groupCode,
                    endpoints: group.endpoints.sort((a, b) => a.label.localeCompare(b.label)),
                })),
            }))
            .sort((a, b) => b.categoryPriority - a.categoryPriority);
    }

    onVersionChange(version: string): void {
        this.siteService.changeVersion(version);
    }

    get versionsForSelectedDomain(): string[] {
        const domain = this.navForm.get('selectedDomain')?.value;
        return domain === 'ally' ? this.allyVersions : this.coreVersions;
    }

    closeNavGroups(): void {
        if (this.navItemsComponent) {
            this.navItemsComponent.collapseAll();
        }
    }

    toggleTheme(): void {
        this.siteService.toggleTheme();
    }

    get resolvedContentType(): string {
        if ( this.contentType === 'docs' ) return 'Docs';
        if( this.selectedDomainVersion.includes('ally_') ) return 'Ally';
        return 'Core';
    }


}